// The detail panel: whatever is selected, spelled out, with links to what it touched.
// `actions` is how the panel reaches the rest of the UI: {jump(rowId, t0, t1, item),
// select(item), showCard(slotName, text)}.

import { el, fmtPs, fmtInt, bytes, esc } from "./dom.js";
import { slotUnit, unitColor, unitsOf } from "./isa.js";
import { coreShort, endStr, dmaShort, issuer, hostLine, hostArgs, flagLabel, slotText } from "./fmt.js";
import { rowKeyOf, releasedWaits } from "./links.js";
import { popcount } from "./timeline.js";
import { openGuide } from "./guide.js";

export class Detail {
  constructor(root, ctx, actions = {}) {
    this.ctx = ctx;
    this.actions = actions;
    this.root = root;
    root.classList.add("detail");
    this.show(null);
    // A bundle shown before its text arrived is shown again when it does.
    if (ctx.disasm) ctx.disasm.onLoad((core, first, n) => {
      const it = this.current;
      if (!it || (it.kind !== "bundle" && it.kind !== "unit")) return;
      if (!core || (it.core === core && it.bi >= first && it.bi < first + n)) this.show(it);
    });
  }

  slotsFor(core, bi) {
    const { disasm, model } = this.ctx;
    const got = disasm ? disasm.get(core, bi) : null;
    if (got) return got.slots;
    return core.hasText ? model.slotsOf(core, bi) : null;
  }

  show(item, at) {
    const r = this.root;
    this.current = item;
    r.textContent = "";
    if (!item) {
      if (at != null) { r.appendChild(el("div", "dim", `cursor at ${fmtPs(at)}`)); return; }
      // Nothing selected yet: where a first-time reader starts, not an empty box.
      r.appendChild(el("div", "strong", "Start here"));
      r.appendChild(el("div", "dim", "Click anything on the timeline — a bundle, a wait, a DMA, a host command — and it is spelled out here."));
      r.appendChild(el("div", "dim", "Double-click a chip in the slice map to open it."));
      const p = el("div", "dim");
      const a = el("a", "guide-open", "How to read this");
      a.href = "#";
      a.onclick = (e) => { e.preventDefault(); openGuide(); };
      p.appendChild(a);
      p.appendChild(document.createTextNode(" — the colours, the rows and the keys (?)."));
      r.appendChild(p);
      return;
    }
    const fn = this["show_" + item.kind];
    if (fn) fn.call(this, item); else r.appendChild(el("div", null, item.kind));
    // The way into the chip page: explicit, on whatever is shown, rather than a
    // double-click somewhere on the timeline.
    const head = r.querySelector(".detail-head");
    if (head && this.actions.expand) {
      const b = el("button", "expand", "open chip page ▸");
      b.title = "the chip page: this stream's listing, the cursor here";
      b.onclick = () => this.actions.expand(item);
      head.appendChild(b);
    }
  }

  // A row of the timeline, clicked: what it is, and the way into its chip page.
  show_row(item) {
    const m = this.ctx.model, c = item.core;
    this.h(c ? c.label : m.chipLabel(item.chip), c ? `${fmtInt(c.n)} bundles` : item.title || "");
    if (c) this.kv([["cycle", `${c.cyclePs.toFixed(0)} ps`], ["waits", fmtInt((this.ctx.links.waitsByRow.get(c.key) || []).length)]]);
  }

  h(text, sub) {
    const d = el("div", "detail-head");
    d.appendChild(el("span", "strong", text));
    if (sub) d.appendChild(el("span", "dim", "  " + sub));
    this.root.appendChild(d);
    return d;
  }
  kv(pairs) {
    const d = el("div", "kv");
    for (const [k, v] of pairs) {
      if (v == null) continue;
      d.appendChild(el("span", "k", k));
      const val = el("span");
      if (v instanceof Node) val.appendChild(v); else val.textContent = v;
      d.appendChild(val);
    }
    this.root.appendChild(d);
    return d;
  }
  link(text, fn) {
    const a = el("a", "act", text);
    a.href = "#";
    a.onclick = (e) => { e.preventDefault(); fn(); };
    return a;
  }
  section(title) { this.root.appendChild(el("div", "detail-sec", title)); }

  slotChips(container, slots, clickable = true, kind = "tc") {
    void clickable;
    if (!slots.length) { container.appendChild(el("span", "dim", "(empty bundle: compiler padding)")); return; }
    for (const s of slots) {
      const hit = slotUnit(s.u, s.i);
      const chip = el("span", "slot");
      chip.style.color = unitColor(hit.unit);
      chip.appendChild(el("span", "sn", s.u));
      chip.appendChild(el("span", "si", slotText(kind, s.u, s.i)));
      chip.title = `${s.u}: ${s.i}`;
      container.appendChild(chip);
    }
  }

  waitLine(w) {
    const { links } = this.ctx;
    const r = links.releaserOf(w);
    const s = el("span");
    s.appendChild(this.link(`wait on flag ${w.idx} of ${coreShort(w.core, w.ci, w.tile)} (chip ${w.chip})`, () => this.actions.select({ kind: "wait", row: rowKeyOf(w.chip, w.core, w.ci, w.tile), wait: w })));
    s.appendChild(document.createTextNode(`  ${w.open ? "open" : fmtPs(w.stallPs) + " stall"}, released by ${r.how}`));
    return s;
  }

  // --- kinds ---------------------------------------------------------------------

  show_bundle(item) {
    const { model } = this.ctx;
    const c = item.core, i = item.bi;
    this.h(`${c.label}  bundle at pc ${c.pc[i]}`, `step ${fmtInt(c.step[i])}, cycle ${fmtInt(model.cycAt(c, i))}, ${fmtPs(c.t[i])}`);
    const slots = el("div", "slots");
    const sl = this.slotsFor(c, i);
    if (sl) this.slotChips(slots, sl, true, c.kind); else slots.appendChild(el("span", "dim", "rendering…"));
    this.root.appendChild(slots);
    const units = model.unitsAtBundle(c, i);
    if (units.length) {
      this.section("unit operations issued here");
      for (const u of units) {
        const extra = Object.entries(u).filter(([k]) => !["step", "unit", "idx", "op", "bi"].includes(k)).map(([k, v]) => `${k}=${JSON.stringify(v)}`).join("  ");
        const d = el("div", "mono");
        const sw = el("span", "swatch"); sw.style.background = unitColor(u.unit);
        d.appendChild(sw); d.appendChild(document.createTextNode(`${u.unit}${u.idx} ${u.op}  ${extra}`));
        this.root.appendChild(d);
      }
    }
    const regs = model.regsAtStep(c, c.step[i]);
    if (regs) { this.section("scalar / predicate registers written"); this.root.appendChild(el("div", "mono", regs)); }
    // A wait parked at this bundle, a DMA issued by it.
    const w = model.waits.find((x) => rowKeyOf(x.chip, x.core, x.ci, x.tile) === c.key && Math.abs(x.t0 - c.t[i]) < c.cyclePs);
    if (w) { this.section("this bundle parked"); this.root.appendChild(this.waitLine(w)); }
    const dmas = model.dmas.filter((d) => d.chip === c.chip && d.byCi === c.ci && (d.byKind === "tc") === (c.kind === "tc") && Math.abs(d.at - c.t[i]) < c.cyclePs);
    if (dmas.length) { this.section("DMA issued here"); for (const d of dmas) this.root.appendChild(this.link(`DMA #${d.id} ${dmaShort(d)}`, () => this.actions.select({ kind: "dma", row: c.key + ".dma", dma: d }))); }
    // Listing context.
    this.section("listing around this pc");
    const list = el("div", "listing");
    const variants = model.pcVariants(c);
    const lo = Math.max(0, c.pc[i] - 8), hi = c.pc[i] + 8;
    for (let pc = lo; pc <= hi; pc++) {
      const vi = variants.get(pc);
      const row = el("div", "lrow" + (pc === c.pc[i] ? " on" : ""));
      row.appendChild(el("span", "lpc", String(pc)));
      if (vi === undefined) row.appendChild(el("span", "dim", "  (not executed on this core)"));
      else {
        const s = el("span");
        const vs = this.slotsFor(c, vi);
        if (vs) this.slotChips(s, vs, false, c.kind); else s.appendChild(el("span", "dim", "…"));
        row.appendChild(s);
        row.onclick = () => {
          const occ = model.pcOccurrences(c, pc);
          const next = occ.find((k) => k > i) ?? occ[0];
          if (next != null) this.actions.jump(c.key, c.t[next] - c.cyclePs * 20, c.t[next] + c.cyclePs * 20, { kind: "bundle", row: c.key, core: c, bi: next });
        };
      }
      list.appendChild(row);
    }
    this.root.appendChild(list);
    // The box shows eight pcs either side; the one at the cursor sits in its middle.
    const on = list.querySelector(".lrow.on");
    if (on) requestAnimationFrame(() => { list.scrollTop = Math.max(0, on.offsetTop - (list.clientHeight - on.offsetHeight) / 2); });
  }

  show_stall(item) {
    const s = item.stall;
    this.h(`${s.reason} stall`, `${s.core.label} at pc ${s.pc}`);
    this.kv([
      ["from", fmtPs(s.t0)], ["until", s.open ? "never released (trace ended)" : fmtPs(s.t1)],
      ["stall", s.open ? "open" : `${fmtPs(s.stallPs)}${s.stall != null ? "  (" + fmtInt(s.stall) + " cycles)" : ""}`],
      ["barrier", s.round != null ? `round ${s.round}, mask 0x${(s.mask || 0).toString(16)}` : null],
      ["flag spun on", s.flag_idx != null ? `flag ${s.flag_idx} of chip ${s.flag_chip} ${coreShort(s.flag_core, s.flag_ci, s.flag_tile)}` : null],
      ["value", s.v != null ? `${s.v}${s.v1 != null ? " → " + s.v1 : ""}` : null],
    ]);
    const bi = this.ctx.model.bundleAt(s.core, s.t0);
    if (bi >= 0) { this.section("the bundle that parked"); this.root.appendChild(this.link(`pc ${s.core.pc[bi]} at ${fmtPs(s.core.t[bi])}`, () => this.actions.select({ kind: "bundle", row: s.core.key, core: s.core, bi }))); }
  }

  show_nop(item) {
    this.h(`${item.core.label}  ${item.nop.n} empty bundles`, `from pc ${item.nop.pc}, ${fmtPs(item.start)} – ${fmtPs(item.end)}`);
    this.root.appendChild(el("p", "dim", "Wholly empty bundles are the compiler's padding for unit latencies: the core issues them at one per cycle and nothing happens."));
  }

  show_unit(item) { this.show_bundle(item); }

  show_wait(item) {
    const { model, links } = this.ctx;
    const w = item.wait, r = links.releaserOf(w);
    this.h(`wait on flag ${w.idx}`, `${coreShort(w.core, w.ci, w.tile)} of chip ${w.chip}`);
    this.kv([
      ["parked at", fmtPs(w.t0) + (w.cyc0 != null ? `  (cycle ${fmtInt(w.cyc0)})` : "")],
      ["released at", w.open ? "never (trace ended first)" : fmtPs(w.t1) + (w.cyc1 != null ? `  (cycle ${fmtInt(w.cyc1)})` : "")],
      ["stall", w.open ? "open" : `${fmtPs(w.stallPs)}${w.stall != null ? "  (" + fmtInt(w.stall) + " of its own cycles)" : ""}`],
      ["flag value", `${w.v} at the park${w.v1 == null ? "" : " → " + w.v1 + " at the release"}`],
    ]);
    this.section("released by");
    const p = el("div");
    p.appendChild(document.createTextNode(r.text));
    this.root.appendChild(p);
    if (r.how === "dma") {
      const d = r.dma;
      const row = d.byKind === "tc" ? `c${d.chip}t${d.byCi}.dma` : d.byKind === "sc" ? `c${d.chip}s${d.byCi}` : `c${d.chip}host`;
      this.root.appendChild(this.link(`DMA #${d.id}: ${dmaShort(d)} by ${issuer(d.by)}, landed ${fmtPs(d.end)}`, () => this.actions.select({ kind: "dma", row, dma: d })));
    } else if (r.how === "core") {
      this.root.appendChild(this.link(`${r.core.label} bundle at pc ${r.core.pc[r.bi]} (${fmtPs(r.core.t[r.bi])})`, () => this.actions.jump(r.core.key, r.core.t[r.bi] - r.core.cyclePs * 20, r.core.t[r.bi] + r.core.cyclePs * 20, { kind: "bundle", row: r.core.key, core: r.core, bi: r.bi })));
    } else if (r.how === "host" && r.host) {
      this.root.appendChild(this.link(`host command #${r.host.id} ${hostLine(r.host)}`, () => this.actions.select({ kind: "host", row: `c${r.host.chip}host`, host: r.host })));
    }
    if (r.flag) {
      const f = r.flag;
      this.root.appendChild(el("div", "dim", `the write: ${f.op} ${f.old} → ${f.new}${f.delta != null ? " (" + (f.delta >= 0 ? "+" : "") + f.delta + ")" : ""} at ${fmtPs(f.at)}`));
    }
    if (r.fromRow && this.actions.arrow) this.actions.arrow(w, r);
    // The bundle that parked.
    {
      const c = model.coreByKey.get(rowKeyOf(w.chip, w.core, w.ci, w.tile));
      if (c) {
        const bi = model.bundleAt(c, w.t0);
        if (bi >= 0) { this.section("the bundle that parked"); this.root.appendChild(this.link(`pc ${c.pc[bi]} at ${fmtPs(c.t[bi])}`, () => this.actions.select({ kind: "bundle", row: c.key, core: c, bi }))); }
      }
    }
  }

  show_flag(item) {
    const { links, model } = this.ctx;
    const f = item.flag;
    this.h(`${f.op} ${flagLabel(f.chip, f.core, f.ci, f.tile, f.idx)}`, fmtPs(f.at));
    this.kv([["value", `${f.old} → ${f.new}${f.delta != null ? " (" + (f.delta >= 0 ? "+" : "") + f.delta + ")" : ""}`], ["done bit", String(f.done)], ["by", f.cmd != null ? `the host (firmware command #${f.cmd})` : "a core or the DMA engine (the trace does not name the writer)"]]);
    const ws = releasedWaits(links, model, "flag", f.i);
    if (ws.length) { this.section("released"); for (const w of ws) this.root.appendChild(this.waitLine(w)); }
  }

  show_dma(item) {
    const { links, model } = this.ctx;
    const d = item.dma;
    this.h(`DMA #${d.id}`, `${bytes(d.bytes)} by ${issuer(d.by)} on chip ${d.chip}${d.cross ? "  ·  cross-chip" : ""}`);
    this.kv([
      ["issued", Number.isNaN(d.at) ? "unknown (no issue record)" : fmtPs(d.at)],
      ["started", Number.isNaN(d.start) ? "never (no completion record)" : fmtPs(d.start) + (d.start > d.at ? `  (queued ${fmtPs(d.start - d.at)} behind the chip's pipe)` : "")],
      ["on the pipe", d.dur == null ? null : fmtPs(d.dur)],
      ["landed", d.open ? "never (trace ended first)" : fmtPs(d.end)],
      ["firmware command", d.cmd == null ? null : `#${d.cmd}`],
    ]);
    this.section(`${d.runs.length} run${d.runs.length === 1 ? "" : "s"}`);
    for (const r of d.runs) this.root.appendChild(el("div", "mono", `${bytes(r.len)}  ${endStr(r.src, true)} @${r.src && r.src.off}  →  ${endStr(r.dst, true)} @${r.dst && r.dst.off}`));
    if (d.credits.length) {
      this.section("credits on landing");
      for (const c of d.credits) this.root.appendChild(el("div", "mono", `${flagLabel(c.chip, c.core, c.ci, c.tile, c.idx)}  ${c.delta != null ? (c.delta >= 0 ? "+" : "") + c.delta : ""}${c.done ? " done" : ""}`));
    }
    const ws = releasedWaits(links, model, "dma", d.i);
    if (ws.length) { this.section("released"); for (const w of ws) this.root.appendChild(this.waitLine(w)); }
  }

  show_host(item) {
    const { links, model } = this.ctx;
    const e = item.host;
    this.h(`chip ${e.chip} host: ${e.ev === "fw_cmd" ? e.kind + " #" + e.id : e.ev}`, hostLine(e));
    const place = e.placed === 0 ? "anchored exactly" : e.placed === 1 ? "interpolated between anchors (approximate)" : "clamped to the run's edge (approximate)";
    this.kv([
      ["virtual time", `${fmtPs(e.ps)}  (${place})`],
      ["wall clock", `${(e.t_ns / 1e6).toFixed(3)} ms after the trace opened`],
      ["arguments", e.ev === "fw_cmd" ? hostArgs(e) : null],
      ["answered", e.done ? `${e.done.status}${e.done.err ? " " + e.done.err : ""}, ${((e.done.t_ns - e.t_ns) / 1e3).toFixed(1)} us later` : null],
    ]);
    if (e.ev === "fw_cmd") {
      const rec = links.byCmd.get(e.chip + ":" + e.id);
      if (rec) {
        if (rec.dispatch) { this.section("started"); const d = rec.dispatch; this.root.appendChild(this.link(`dispatch #${d.run_id} on ${d.coreKey}: ${fmtInt(d.bundles)} bundles, ${d.open ? "still running" : fmtPs(d.t1 - d.t0)}`, () => this.actions.jump(d.coreKey, d.t0, d.open ? model.tEnd : d.t1))); }
        if (rec.dmas.length) { this.section("DMAs"); for (const d of rec.dmas) this.root.appendChild(this.link(`DMA #${d.id} ${dmaShort(d)}`, () => this.actions.select({ kind: "dma", row: `c${d.chip}host`, dma: d }))); }
        if (rec.flags.length) { this.section("flag writes"); for (const f of rec.flags) this.root.appendChild(el("div", "mono", `${f.op} ${flagLabel(f.chip, f.core, f.ci, f.tile, f.idx)} ${f.old} → ${f.new}`)); }
      }
      if (e.kind === "run" && e.op === "control_tile") this.root.appendChild(el("p", "dim", `SparseCore ${e.ci} dispatch: ${popcount(e.tile_mask)} tiles (mask 0x${(e.tile_mask || 0).toString(16)}), flags ${e.flags}.`));
    }
  }

  show_scdispatch(item) {
    const d = item.sc;
    this.h(`SparseCore ${d.ci} dispatch on chip ${d.chip}`, `${fmtPs(d.ps)}${d.placed === 0 ? "" : " (placed by interpolation)"}`);
    const grid = el("div", "tiles");
    for (let t = 0; t < 16; t++) {
      const c = el("span", "tile" + ((d.tileMask >> t) & 1 ? " on" : ""), String(t));
      grid.appendChild(c);
    }
    this.root.appendChild(grid);
    this.kv([["flags", String(d.flags)], ["host command", `#${d.host.id}`]]);
    this.root.appendChild(el("p", "dim", "SparseCore tiles leave no instruction trace in this simulator build; what they did is visible through the flags they wrote and the DMAs they issued."));
  }

  show_dispatch(item) {
    const d = item.dispatch;
    this.h(`dispatch #${d.run_id} on ${d.coreKey}`, `command #${d.cmd}`);
    this.kv([["pc", `${d.pc0} → ${d.pc1 == null ? "?" : d.pc1}`], ["cycles", d.cyc1 == null ? "open" : fmtInt(d.cyc1 - d.cyc0)], ["time", d.open ? "open" : fmtPs(d.t1 - d.t0)], ["bundles", `${fmtInt(d.bundles)} issued, ${fmtInt(d.nops)} empty`]]);
  }

  show_bin(item) {
    const b = item.bin;
    this.h(item.core ? item.core.label : `chip ${item.chip}`, `around ${fmtPs(item.t0)}`);
    this.kv([["issuing", (b.busy * 100).toFixed(0) + "%"], ["waiting", (b.wait * 100).toFixed(0) + "%"], ["padding", (b.nop * 100).toFixed(0) + "%"], ["bundles", fmtInt(b.count)], ["units used", unitsOf(b.mask).join(" ") || "none"]]);
    this.root.appendChild(el("p", "dim", "Zoom in (ctrl+wheel, or double-click) to see the bundles themselves."));
  }
}
